/**
 * Phase 6 Plan 02 — AuditTrailTable (HIST-5.3, D-136).
 *
 * Lazy-loaded table inside InspectionRow expanded state. Columns: When · Who
 * · Field · Before · After. Server returns rows ordered ASC by edited_at +
 * id ASC tie-break (HIST-5.3 — oldest-first; chronological history).
 *
 * Type-aware before/after formatters:
 *   - boolean → Yes / No (LABEL_BOOLEAN_YES / LABEL_BOOLEAN_NO if available;
 *     else literal Y/N).
 *   - null    → em-dash (—).
 *   - string  → coating values map to LABEL_COATING_BITUMEN / _EPOXY; other
 *     strings render as <bdi>{string}</bdi> (CP4-A: server allow-list rejects
 *     credential field names at the write site).
 *
 * @SpecRef("5.3")
 */
import { FormattedMessage, FormattedDate, FormattedTime } from "react-intl";
import { useInspectionAuditTrail } from "../useInspectionAuditTrail";
import type { InspectionAuditRow } from "../../../api/inspections";

interface AuditTrailTableProps {
  inspectionId: number;
}

function renderValue(v: boolean | string | null) {
  if (v === null) return <span aria-label="empty">—</span>;
  if (typeof v === "boolean") {
    // Yes/No is a common universal pattern; pre-existing LABEL_BOOLEAN_*
    // codes do not exist in the catalog. Render literal text — CP4 safe.
    return v ? "Yes" : "No";
  }
  // String — coating values get localized; other strings render verbatim.
  if (v === "bitumen") return <FormattedMessage id="LABEL_COATING_BITUMEN" />;
  if (v === "epoxy") return <FormattedMessage id="LABEL_COATING_EPOXY" />;
  return <bdi>{v}</bdi>;
}

export function AuditTrailTable({ inspectionId }: AuditTrailTableProps) {
  // The caller (InspectionRow expanded state) only mounts this component when
  // pm_edit_count > 0; passing `enabled=true` directly is correct.
  const query = useInspectionAuditTrail(inspectionId, true);
  const rows: InspectionAuditRow[] = query.data?.rows ?? [];
  return (
    <section
      className="mbs-md"
      data-testid={`audit-trail-${inspectionId}`}
    >
      <h5 className="text-xs font-semibold text-on-surface-variant mbe-xxs">
        <FormattedMessage id="HIST_AUDIT_TRAIL_HEADING" />
      </h5>
      <table data-density="compact" className="w-full bg-surface-raised border border-divider rounded-md overflow-hidden text-xs">
        <thead className="bg-surface-variant">
          <tr>
            <th className="ps-sm pe-sm pbs-sm pbe-sm text-start font-semibold text-on-surface-variant">
              <FormattedMessage id="HIST_AUDIT_COL_WHEN" />
            </th>
            <th className="ps-sm pe-sm pbs-sm pbe-sm text-start font-semibold text-on-surface-variant">
              <FormattedMessage id="HIST_AUDIT_COL_WHO" />
            </th>
            <th className="ps-sm pe-sm pbs-sm pbe-sm text-start font-semibold text-on-surface-variant">
              <FormattedMessage id="HIST_AUDIT_COL_FIELD" />
            </th>
            <th className="ps-sm pe-sm pbs-sm pbe-sm text-start font-semibold text-on-surface-variant">
              <FormattedMessage id="HIST_AUDIT_COL_BEFORE" />
            </th>
            <th className="ps-sm pe-sm pbs-sm pbe-sm text-start font-semibold text-on-surface-variant">
              <FormattedMessage id="HIST_AUDIT_COL_AFTER" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-bs border-divider even:bg-surface/30"
              data-testid={`audit-row-${row.id}`}
            >
              <td className="ps-sm pe-sm pbs-xs pbe-xs text-on-surface">
                <bdi>
                  <FormattedDate
                    value={row.edited_at}
                    year="numeric"
                    month="2-digit"
                    day="2-digit"
                  />
                  {" "}
                  <FormattedTime value={row.edited_at} hour="2-digit" minute="2-digit" hour12={false} />
                </bdi>
              </td>
              <td className="ps-sm pe-sm pbs-xs pbe-xs text-on-surface">
                <FormattedMessage
                  id={row.edited_by === "pm" ? "HIST_AUDIT_WHO_PM" : "HIST_AUDIT_WHO_WORKER"}
                />
              </td>
              <td className="ps-sm pe-sm pbs-xs pbe-xs text-on-surface font-mono">
                <bdi>{row.field_name}</bdi>
              </td>
              <td className="ps-sm pe-sm pbs-xs pbe-xs text-on-surface-variant">
                {renderValue(row.before_value)}
              </td>
              <td className="ps-sm pe-sm pbs-xs pbe-xs text-on-surface font-semibold">
                {renderValue(row.after_value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
